import { Button as MuiButton } from '@mui/material';

/**
 * @typedef {Object} ButtonProps
 * @property {React.ReactNode} children - Button content
 * @property {string} [variant] - Button variant (default: 'contained')
 * @property {string} [color] - Button color (default: 'primary')
 * @property {string} [size] - Button size (default: 'medium')
 * @property {boolean} [disabled] - Whether button is disabled
 * @property {() => void} [onClick] - Click handler
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [sx] - Additional Material-UI sx props
 * @property {string} [type] - Button type (submit, button, reset)
 */

/**
 * Button component for consistent button styling
 *
 * A reusable button component that provides consistent styling,
 * hover effects, and behavior across the application.
 *
 * @param {ButtonProps} props - Component props
 * @returns {JSX.Element} Button component
 */
function Button({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  onClick,
  className,
  sx = {},
  type = 'button',
}) {
  return (
    <MuiButton
      variant={variant}
      color={color}
      size={size}
      disabled={disabled}
      onClick={onClick}
      className={className}
      type={type}
      sx={{
        borderRadius: '40px',
        padding: '14px 32px',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        ...sx,
      }}
    >
      {children}
    </MuiButton>
  );
}

export default Button;
